import type {
  FormSchemaVersion,
  BespokeTemplateResponse,
  BespokeTemplateRevision,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { tryParseLayoutJson } from '../../components/oasis/bespoke/layout/tryParseLayoutJson';
import { collectLayoutReferencedQids } from '../../components/oasis/bespoke/layout/collectLayoutReferencedQids';
import { collectLayoutRenderableFieldQids } from '../../components/oasis/bespoke/layout/collectLayoutRenderableFieldQids';
import {
  collectLayoutPlainCssHiddenQids,
  type LayoutPlainCssHiddenQid,
} from '../../components/oasis/bespoke/layout/collectLayoutPlainCssHiddenQids';
import { collectLayoutPartialRadioValuesByQid } from '../../components/oasis/bespoke/layout/collectLayoutRadioOptionValuesByQid';
import { computeMissingCmsVisibleLabels } from '../../components/oasis/bespoke/layout/letteredCmsVisibleLabelCoverage';
import {
  collectLayoutSchemaGateIssues,
  formatLayoutSchemaGateIssueSummary,
} from '../../components/oasis/bespoke/layout/collectLayoutSchemaGateIssues';
import {
  computeRadioOptionSchemaMismatches,
  type RadioOptionSchemaMismatch,
} from '../../components/oasis/bespoke/layout/radioOptionSchemaCoverage';
import { collectSectionCoverageQids } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import type { Question } from '../../components/oasis/types';
import { truncateJoinedList } from '../../utils/formatting';
import type {
  FormTreeQuestion,
  LayoutValidationResult,
  SectionLayoutCoverageIssues,
  SectionLayoutState,
} from './formSchemaTypes';
import { formSchemaVersionPinKeyFromTemplate } from './formSchemaVersionPin';

/**
 * User-facing message for a bespoke form-schema failure.
 *
 * Prefers API `detail` / network hints via {@link getUserFacingApiMessage}.
 * That helper intentionally never surfaces `Error.message`, so local throws
 * (e.g. layout validation before PATCH) fall through to `err.message` here.
 *
 * The `err.message` fallback is gated on the error having no HTTP status, so
 * only genuinely local throws surface their message. HTTP errors (e.g. a 5xx
 * whose `getUserFacingApiMessage` intentionally returns nothing, or an Axios
 * error whose `.message` is "Request failed with status code 500") keep the
 * generic copy instead of leaking a raw transport string.
 */
export function bespokeApiErrorMessage(err: unknown): string {
  const fromApi = getUserFacingApiMessage(err, '');
  if (fromApi) return fromApi;
  if (
    getHttpStatus(err) === undefined &&
    err instanceof Error &&
    err.message.trim()
  ) {
    return err.message.trim();
  }
  return 'Something went wrong. Please try again.';
}

export function isEffective(v: FormSchemaVersion): boolean {
  const now = new Date();
  const from = v.effective_from ? new Date(v.effective_from) : null;
  const until = v.effective_until ? new Date(v.effective_until) : null;
  if (from && now < from) return false;
  if (until && now > until) return false;
  return true;
}

export function schemaVersionKey(v: FormSchemaVersion): string {
  return `${v.name}::${v.version ?? ''}`;
}

/** Default name for a new blank section layout; ISO suffix avoids create collisions. */
export function defaultBlankSectionLayoutName(
  sectionDisplayName: string
): string {
  return `${sectionDisplayName} layout ${new Date().toISOString()}`;
}

export function schemaVersionLabel(v: FormSchemaVersion): string {
  return v.version ? `${v.name} (${v.version})` : v.name;
}

/** Stable key for global layout-generation status (schema version name + section). Uses `::` like {@link schemaVersionKey}. */
export function layoutGenerationKey(
  schemaVersionName: string,
  sectionName: string
): string {
  return `${schemaVersionName}::${sectionName}`;
}

/** Editor draft key — scoped by visit type so drafts do not bleed across form types. */
export function sectionEditorDraftKey(
  rogerDocFormType: RogerDocFormType,
  schemaVersionName: string,
  sectionName: string
): string {
  return `${rogerDocFormType}::${layoutGenerationKey(schemaVersionName, sectionName)}`;
}

/**
 * Whether the editor draft is the section's active layout slot (Live/Paused primary).
 * Matches the "Active template" row in the Layout versions modal.
 */
export function isEditingActiveLayoutVersion(args: {
  editingTemplateId: string | null;
  activeTemplateId: string | null | undefined;
  layoutStatus: 'live' | 'paused' | 'standby';
}): boolean {
  const { editingTemplateId, activeTemplateId, layoutStatus } = args;
  if (editingTemplateId == null || activeTemplateId == null) {
    return false;
  }
  return (
    editingTemplateId === activeTemplateId &&
    (layoutStatus === 'live' || layoutStatus === 'paused')
  );
}

/**
 * True when a per-section Redux draft points at a non-active template row.
 * Section nav without `layoutTemplate` in the URL should reset to active instead.
 */
export function isPersistedInactiveLayoutDraft(
  draft: { editingTemplateId: string | null } | undefined,
  active: BespokeTemplateResponse | null | undefined
): boolean {
  if (!draft?.editingTemplateId) return false;
  if (!active) return true;
  return draft.editingTemplateId !== active.id;
}

/**
 * Order-independent fingerprint of a `/bespoke-templates/revisions` poll
 * response — changes whenever any row's identity, status, or content
 * signature changes. Compared against the previous poll's fingerprint so the
 * live-refresh loop can skip a no-op tick instead of re-deriving the editor
 * target on every interval.
 */
export function bespokeTemplateRevisionsFingerprint(
  revisions: readonly BespokeTemplateRevision[]
): string {
  return revisions
    .map(
      r =>
        `${r.id}:${r.updated_at}:${r.status}:${r.layout_field_qid_signature ?? ''}`
    )
    .sort()
    .join('|');
}

/**
 * Per-template MCP-push state (EMR-277 UI indicators). `acknowledged` becomes
 * true once the template becomes the open editor target — that clears the
 * section-tab / versions-modal dot but the entry is kept (not deleted) so the
 * editor header pill can keep showing "Updated remotely <time>" until a local
 * save. A later push to the same template flips `acknowledged` back to false.
 */
export interface RemotePushInfo {
  updatedAt: string;
  acknowledged: boolean;
}

export type RemotePushByTemplateId = Record<string, RemotePushInfo>;

/** Last-seen revision metadata per template row for remote-push polling. */
export type RemotePushRevisionSnapshot = {
  updatedAt: string;
  layoutFieldQidSignature: string;
  status: BespokeTemplateRevision['status'];
};

/**
 * Diff one `/bespoke-templates/revisions` poll against the previously seen
 * snapshot per row to detect an MCP-pushed layout change — either an update
 * whose `layout_field_qid_signature` changed, *or* a brand-new row (MCP
 * `create`) that showed up since the last poll, *or* the same status row whose
 * `updated_at` moved forward with an unchanged signature (layout-only MCP
 * edit). Status-only edits (e.g. demoting the prior live row when another
 * template is set active) bump `updated_at` but change `status`, so they are
 * ignored. Ids this tab just saved itself are excluded via `ownSaveTemplateIds`.
 *
 * `hasBaseline` must be `false` only for the very first poll after a
 * formType/version switch (or initial mount): every row on that poll is
 * pre-existing with nothing to compare against, so none are flagged — a
 * freshly-opened section must not light every existing row's dot just
 * because we've never polled it before. Pass `true` on every later poll for
 * the same pin; a row missing from `previousById` at that point genuinely
 * appeared between two polls and must flag.
 */
export function diffRevisionsForRemotePush(args: {
  revisions: readonly BespokeTemplateRevision[];
  previousById: Readonly<Record<string, RemotePushRevisionSnapshot>>;
  ownSaveTemplateIds: ReadonlySet<string>;
  hasBaseline: boolean;
}): {
  nextById: Record<string, RemotePushRevisionSnapshot>;
  pushed: { id: string; updatedAt: string }[];
} {
  const { revisions, previousById, ownSaveTemplateIds, hasBaseline } = args;
  const nextById: Record<string, RemotePushRevisionSnapshot> = {};
  const pushed: { id: string; updatedAt: string }[] = [];
  for (const row of revisions) {
    const signature = row.layout_field_qid_signature ?? '';
    nextById[row.id] = {
      updatedAt: row.updated_at,
      layoutFieldQidSignature: signature,
      status: row.status,
    };
    if (ownSaveTemplateIds.has(row.id)) continue;

    const prev = previousById[row.id];
    if (prev === undefined) {
      // Brand-new row: only a real push once a baseline poll has already
      // run for this pin (see docstring) — the very first poll must not
      // flag pre-existing rows it has simply never seen before.
      if (hasBaseline) {
        pushed.push({ id: row.id, updatedAt: row.updated_at });
      }
      continue;
    }

    if (prev.layoutFieldQidSignature !== signature) {
      pushed.push({ id: row.id, updatedAt: row.updated_at });
    } else if (
      prev.updatedAt !== row.updated_at &&
      prev.status === row.status
    ) {
      pushed.push({ id: row.id, updatedAt: row.updated_at });
    }
  }
  return { nextById, pushed };
}

export type RevisionsPollFingerprintRefreshPlan =
  | { kind: 'fingerprint_unchanged' }
  | { kind: 'defer_dirty_draft'; nextFingerprint: string }
  | {
      kind: 'apply';
      nextFingerprint: string;
      templateToSync: BespokeTemplateResponse | null;
    };

/**
 * After a revisions poll detects a fingerprint change and the catalog has been
 * refetched, decide whether to sync the open editor or defer so unsaved edits
 * are not clobbered. When deferring, the caller must not advance the stored
 * fingerprint so the next poll retries.
 */
export function planRevisionsPollFingerprintRefresh(args: {
  revisions: readonly BespokeTemplateRevision[];
  previousFingerprint: string | null;
  editingTemplateId: string | null | undefined;
  isDraftDirty: boolean;
  refreshedTemplates: readonly BespokeTemplateResponse[];
}): RevisionsPollFingerprintRefreshPlan {
  const nextFingerprint = bespokeTemplateRevisionsFingerprint(args.revisions);
  if (nextFingerprint === args.previousFingerprint) {
    return { kind: 'fingerprint_unchanged' };
  }

  if (args.editingTemplateId) {
    const latest =
      args.refreshedTemplates.find(t => t.id === args.editingTemplateId) ??
      null;
    if (latest && args.isDraftDirty) {
      return { kind: 'defer_dirty_draft', nextFingerprint };
    }
    if (latest) {
      return {
        kind: 'apply',
        nextFingerprint,
        templateToSync: latest,
      };
    }
  }

  return { kind: 'apply', nextFingerprint, templateToSync: null };
}

/**
 * Pure reducer for `remotePushByTemplateId`: records newly-detected pushes
 * (always unacknowledged, even if the id was previously acked — a second push
 * to an already-opened standby must re-light the dot) and drops entries for
 * ids no longer present in the latest poll (deleted upstream).
 */
export function applyRemotePushDelta(
  prev: RemotePushByTemplateId,
  args: {
    pushed: readonly { id: string; updatedAt: string }[];
    presentTemplateIds: ReadonlySet<string>;
  }
): RemotePushByTemplateId {
  const { pushed, presentTemplateIds } = args;
  let changed = false;
  const next: RemotePushByTemplateId = {};
  for (const [id, info] of Object.entries(prev)) {
    if (presentTemplateIds.has(id)) {
      next[id] = info;
    } else {
      changed = true;
    }
  }
  for (const { id, updatedAt } of pushed) {
    next[id] = { updatedAt, acknowledged: false };
    changed = true;
  }
  return changed ? next : prev;
}

/** Mark a template's push as seen (clears the dot; keeps the pill data). No-op if absent/already acked. */
export function acknowledgeRemotePush(
  prev: RemotePushByTemplateId,
  templateId: string | null | undefined
): RemotePushByTemplateId {
  if (!templateId) return prev;
  const info = prev[templateId];
  if (!info || info.acknowledged) return prev;
  return { ...prev, [templateId]: { ...info, acknowledged: true } };
}

/** Drop a template's push entirely (dot + pill) after this tab saves it locally. */
export function clearRemotePush(
  prev: RemotePushByTemplateId,
  templateId: string | null | undefined
): RemotePushByTemplateId {
  if (!templateId || !(templateId in prev)) return prev;
  const next = { ...prev };
  delete next[templateId];
  return next;
}

/** Most recent unacknowledged push among a section's template ids, or `null`. Drives the section-tab dot + tooltip. */
export function latestUnacknowledgedRemotePush(
  remotePushByTemplateId: RemotePushByTemplateId,
  templateIds: readonly string[]
): RemotePushInfo | null {
  let latest: RemotePushInfo | null = null;
  for (const id of templateIds) {
    const info = remotePushByTemplateId[id];
    if (!info || info.acknowledged) continue;
    if (!latest || info.updatedAt > latest.updatedAt) latest = info;
  }
  return latest;
}

/** Unacknowledged push for one template id, or `null`. Drives the Layout versions modal row dot. */
export function unacknowledgedRemotePush(
  remotePushByTemplateId: RemotePushByTemplateId,
  templateId: string | null | undefined
): RemotePushInfo | null {
  if (!templateId) return null;
  const info = remotePushByTemplateId[templateId];
  return info && !info.acknowledged ? info : null;
}

/** "2:14 PM" in the browser's local time zone — this is a live in-session indicator, not a stored clinical timestamp. */
export function formatRemotePushTimeLabel(updatedAt: string): string {
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Compact header label + tooltip when {@link isEditingActiveLayoutVersion} is false. */
export function inactiveLayoutVersionIndicator(args: {
  editingTemplateId: string | null;
  activeTemplateId: string | null | undefined;
  layoutStatus: 'live' | 'paused' | 'standby';
}): { short: string; detail: string } | null {
  if (isEditingActiveLayoutVersion(args)) {
    return null;
  }
  if (args.editingTemplateId == null) {
    return {
      short: 'Draft',
      detail: 'New layout — not active until promoted to Live.',
    };
  }
  if (
    args.activeTemplateId != null &&
    args.editingTemplateId !== args.activeTemplateId
  ) {
    return {
      short: 'Inactive',
      detail: 'Not the active layout for this section.',
    };
  }
  if (args.layoutStatus === 'standby') {
    return {
      short: 'Standby',
      detail: 'Not used in visits until set to Live.',
    };
  }
  return {
    short: 'Inactive',
    detail: 'Not the active layout for this section.',
  };
}

/** @internal Unit separator — must match backend ``bespoke_layout_field_signature._UNIT_SEP``. */
const LAYOUT_FIELD_QID_SIG_SEP = '\x1f';

/**
 * SHA-256 hex of sorted unique coverage QIDs — matches persisted
 * ``layout_field_qid_signature`` on bespoke templates (answer controls only via layout collection).
 */
export async function layoutFieldQidSignatureHexFromCoverageQids(
  qids: string[]
): Promise<string> {
  const canonical = [...new Set(qids)].sort().join(LAYOUT_FIELD_QID_SIG_SEP);
  const enc = new TextEncoder().encode(canonical);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return [...new Uint8Array(buf)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function layoutFieldQidSignatureHexFromQuestions(
  questions: FormTreeQuestion[]
): Promise<string> {
  return layoutFieldQidSignatureHexFromCoverageQids(
    collectSectionCoverageQids(questions as Question[])
  );
}

export function sectionQidSignature(questions: FormTreeQuestion[]): string {
  return collectSectionCoverageQids(questions as Question[])
    .slice()
    .sort()
    .join('');
}

export function formatLayoutPlainCssHiddenSummary(
  issue: LayoutPlainCssHiddenQid
): string {
  return `${issue.qid} (${issue.reason})`;
}

export function formatRadioOptionSchemaMismatchSummary(
  issue: RadioOptionSchemaMismatch
): string {
  const parts: string[] = [issue.qid];
  if (issue.missingValues.length > 0) {
    parts.push(`missing: ${issue.missingValues.join(', ')}`);
  }
  if (issue.extraValues.length > 0) {
    parts.push(`extra: ${issue.extraValues.join(', ')}`);
  }
  return parts.join(' — ');
}

function layoutTextFromInput(
  layoutInput: string | unknown | null | undefined
): string {
  if (typeof layoutInput === 'string') return layoutInput;
  if (layoutInput == null) return '[]';
  return JSON.stringify(layoutInput);
}

export function collectSectionLayoutCoverageIssues(
  layoutInput: string | unknown | null | undefined,
  questions: FormTreeQuestion[]
): SectionLayoutCoverageIssues {
  // Pseudo wrappers are already stripped server-side, so the section schema
  // here contains only real, renderable questions.
  const bespokeQuestions = questions as Question[];
  const parsed = tryParseLayoutJson(layoutTextFromInput(layoutInput));
  const schemaQids = collectSectionCoverageQids(bespokeQuestions);

  if (!parsed.ok) {
    return {
      uncoveredQids: [...schemaQids].sort((a, b) => a.localeCompare(b)),
      strayQids: [],
      radioMismatches: [],
      cssHiddenQids: [],
      missingCmsVisibleLabels: [],
      schemaGateIssues: [],
      parseOk: false,
      parseError: parsed.error,
    };
  }

  const layout = parsed.layout;
  try {
    const schemaQidSet = new Set(schemaQids);
    const layoutFieldIds = collectLayoutRenderableFieldQids(layout);
    const uncoveredQids = schemaQids
      .filter(qid => !layoutFieldIds.has(qid))
      .sort((a, b) => a.localeCompare(b));
    const strayQids = [...collectLayoutReferencedQids(layout)]
      .filter(qid => !schemaQidSet.has(qid))
      .sort((a, b) => a.localeCompare(b));
    const radioMismatches = computeRadioOptionSchemaMismatches(
      bespokeQuestions,
      collectLayoutPartialRadioValuesByQid(layout, bespokeQuestions)
    );
    const cssHiddenQids = collectLayoutPlainCssHiddenQids(layout, schemaQids);
    const missingCmsVisibleLabels = computeMissingCmsVisibleLabels(
      bespokeQuestions,
      layout,
      layoutFieldIds
    );
    const schemaGateIssues = collectLayoutSchemaGateIssues(
      layout,
      bespokeQuestions
    );

    return {
      uncoveredQids,
      strayQids,
      radioMismatches,
      cssHiddenQids,
      missingCmsVisibleLabels,
      schemaGateIssues,
      parseOk: true,
      layout,
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Layout structure is invalid.';
    return {
      uncoveredQids: [...schemaQids].sort((a, b) => a.localeCompare(b)),
      strayQids: [],
      radioMismatches: [],
      cssHiddenQids: [],
      missingCmsVisibleLabels: [],
      schemaGateIssues: [],
      parseOk: false,
      parseError: message,
    };
  }
}

export function sectionHasLayoutCoverageGaps(
  issues: SectionLayoutCoverageIssues
): boolean {
  return (
    !issues.parseOk ||
    issues.uncoveredQids.length > 0 ||
    issues.strayQids.length > 0 ||
    issues.radioMismatches.length > 0 ||
    issues.cssHiddenQids.length > 0 ||
    issues.missingCmsVisibleLabels.length > 0 ||
    issues.schemaGateIssues.length > 0
  );
}

export function validateLayoutJsonForSection(
  raw: string,
  questions: FormTreeQuestion[]
): LayoutValidationResult {
  const issues = collectSectionLayoutCoverageIssues(raw, questions);
  if (!issues.parseOk) {
    return { ok: false, error: issues.parseError };
  }

  if (issues.strayQids.length > 0) {
    return {
      ok: false,
      error: `QIDs present in Roger Layout Schema that do not belong (${issues.strayQids.length}): ${truncateJoinedList(
        issues.strayQids,
        5
      )}.`,
    };
  }

  if (issues.uncoveredQids.length > 0) {
    return {
      ok: false,
      error: `Required QIDs missing from Roger Layout Schema (${issues.uncoveredQids.length}): ${truncateJoinedList(issues.uncoveredQids, 12)}.`,
    };
  }

  if (issues.radioMismatches.length > 0) {
    const detail = issues.radioMismatches
      .slice(0, 6)
      .map(formatRadioOptionSchemaMismatchSummary);
    const suffix =
      issues.radioMismatches.length > 6
        ? ` (+${issues.radioMismatches.length - 6} more QIDs)`
        : '';
    return {
      ok: false,
      error: `Radio table codes vs schema values (${issues.radioMismatches.length}): ${detail.join('; ')}${suffix}.`,
    };
  }

  if (issues.cssHiddenQids.length > 0) {
    const detail = issues.cssHiddenQids
      .slice(0, 6)
      .map(formatLayoutPlainCssHiddenSummary);
    const suffix =
      issues.cssHiddenQids.length > 6
        ? ` (+${issues.cssHiddenQids.length - 6} more QIDs)`
        : '';
    return {
      ok: false,
      error: `Required QIDs hidden with CSS (${issues.cssHiddenQids.length}): ${detail.join('; ')}${suffix}. Remove display:none, visibility:hidden, opacity:0, hidden, invisible, opacity-0, etc. from required fields and their wrappers.`,
    };
  }

  if (issues.schemaGateIssues.length > 0) {
    const detail = issues.schemaGateIssues
      .slice(0, 6)
      .map(formatLayoutSchemaGateIssueSummary);
    const suffix =
      issues.schemaGateIssues.length > 6
        ? ` (+${issues.schemaGateIssues.length - 6} more QIDs)`
        : '';
    return {
      ok: false,
      error: `Schema parent/child gating (${issues.schemaGateIssues.length}): ${detail.join('; ')}${suffix}. Top-level schema questions must render outside conditional and disabled-when blocks; gated nested subitems may only reference schema ancestors with matching subitems answer keys. For disabled-when, whenAll means "keep enabled while" (same eq/includes as conditional), not the opposite answer.`,
    };
  }

  if (issues.missingCmsVisibleLabels.length > 0) {
    const suffix =
      issues.missingCmsVisibleLabels.length > 12
        ? ` (+${issues.missingCmsVisibleLabels.length - 12} more)`
        : '';
    return {
      ok: false,
      error: `Lettered CMS item ids missing visible layout labels (${issues.missingCmsVisibleLabels.length}): ${truncateJoinedList(issues.missingCmsVisibleLabels, 12)}${suffix}. Add each base id once as user-visible text, preferably in parentheses e.g. (GG0100) in a static or section-label—not per-answer suffixes (GG0100A, GG0100B) on field labelOverrides.`,
    };
  }

  return { ok: true, layout: issues.layout };
}

export function getSectionLayoutState(
  template: BespokeTemplateResponse | undefined,
  questions: FormTreeQuestion[]
): SectionLayoutState {
  if (!template) {
    return { kind: 'missing' };
  }
  if (!template.layout_json) {
    return {
      kind: 'invalid',
      template,
      reason: 'Template has no Roger Layout Schema.',
    };
  }
  const validation = validateLayoutJsonForSection(
    JSON.stringify(template.layout_json),
    questions
  );
  if (!validation.ok) {
    return { kind: 'invalid', template, reason: validation.error };
  }
  return { kind: 'valid', template };
}

/**
 * Single source of truth for "is this section safe to port to other live
 * forms via Copy to similar sections": a layout that's missing, invalid, has
 * unsaved edits, or has no loaded question schema must not be spliced into
 * other live templates. Shared by the section nav's menu-item `disabled`
 * check and the dialog-opening handler so the two can't drift apart.
 */
export function isEligibleForSectionPortCopy(options: {
  layoutState: SectionLayoutState;
  dirty: boolean;
  hasQuestions: boolean;
}): boolean {
  const { layoutState, dirty, hasQuestions } = options;
  return layoutState.kind === 'valid' && !dirty && hasQuestions;
}

/** Live wins over paused for the active template slot (versions table + enabled map). */
export function pickPrimaryBespokeTemplate(
  templates: readonly BespokeTemplateResponse[]
): BespokeTemplateResponse | undefined {
  return (
    templates.find(t => t.status === 'live') ??
    templates.find(t => t.status === 'paused') ??
    undefined
  );
}

function isPrimaryBespokeStatus(
  status: BespokeTemplateResponse['status']
): boolean {
  return status === 'live' || status === 'paused';
}

/** Active card + standby list for one section's template rows (Layout versions modal). */
export function resolveBespokePrimaryAndStandby(
  templates: readonly BespokeTemplateResponse[]
): {
  primary: BespokeTemplateResponse | undefined;
  standby: BespokeTemplateResponse[];
} {
  const primary = pickPrimaryBespokeTemplate(templates);
  const standby = templates
    .filter(
      t =>
        t.status === 'standby' ||
        (primary != null &&
          isPrimaryBespokeStatus(t.status) &&
          t.id !== primary.id)
    )
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  return { primary, standby };
}

/**
 * Guard a bespoke save response body. Every mutation that now patches local
 * state (create/update/copy/status/AI-generate) reads the saved row from the
 * response, so a 2xx with no parsed template (proxy hiccup / unexpected client
 * shape) must fail loudly here instead of silently celebrating success and
 * leaving the on-screen list pointing at the stale active layout.
 */
export function requireSavedBespokeTemplate(
  data: unknown
): BespokeTemplateResponse {
  if (!data) {
    throw new Error('Layout was saved but the server returned no template.');
  }
  return data as BespokeTemplateResponse;
}

/**
 * Merge saved bespoke rows into the local list without a full refetch.
 *
 * Untouched rows keep their exact object identity, which is what stops the open
 * section editor from re-fetching its RogerDoc question schema after every
 * save/copy. We also mirror the backend's "one primary per section" rule: when
 * a saved row is live/paused, every other live/paused row in the same
 * section + schema-version pin is demoted to standby locally. Without this the
 * list would show two active rows and keep pointing the section at the stale
 * one (the server already demoted the old row, but the client never heard).
 *
 * KEEP IN SYNC with the backend authority for this rule:
 * `_demote_other_primaries_in_section` / `_is_primary_status` in
 * `backend/app/crud/bespoke_template.py`. Both must agree on which statuses
 * count as primary (live/paused) and how a section is scoped
 * (form_type + schema_version_name + form_schema_version + section_name).
 * If the backend rule changes and this twin doesn't, the UI silently points a
 * section at the wrong active layout until a full reload heals it.
 */
/**
 * Ids of live/paused rows in the same section pin that `upsertBespokeTemplatesLocal`
 * would demote when `promoted` becomes primary. Used to suppress false remote-push
 * flags and clear dots after a local "Set active" from this tab.
 */
export function demotedPrimarySiblingIdsForPromotion(
  catalog: readonly BespokeTemplateResponse[],
  templateBeingPromoted: BespokeTemplateResponse
): string[] {
  const pinKey = formSchemaVersionPinKeyFromTemplate(templateBeingPromoted);
  return catalog
    .filter(
      row =>
        row.id !== templateBeingPromoted.id &&
        row.section_name === templateBeingPromoted.section_name &&
        (row.status === 'live' || row.status === 'paused') &&
        formSchemaVersionPinKeyFromTemplate(row) === pinKey
    )
    .map(row => row.id);
}

export function upsertBespokeTemplatesLocal(
  prev: readonly BespokeTemplateResponse[],
  saved: readonly BespokeTemplateResponse[]
): BespokeTemplateResponse[] {
  if (saved.length === 0) return [...prev];
  const byId = new Map(prev.map(t => [t.id, t]));
  for (const s of saved) byId.set(s.id, s);
  for (const s of saved) {
    if (s.status !== 'live' && s.status !== 'paused') continue;
    const savedPinKey = formSchemaVersionPinKeyFromTemplate(s);
    for (const [id, t] of byId) {
      if (
        id !== s.id &&
        t.section_name === s.section_name &&
        (t.status === 'live' || t.status === 'paused') &&
        formSchemaVersionPinKeyFromTemplate(t) === savedPinKey
      ) {
        byId.set(id, { ...t, status: 'standby' });
      }
    }
  }
  return [...byId.values()];
}

/** Remove a bespoke row from the local list by id (post-delete patch). */
export function removeBespokeTemplateLocal(
  prev: readonly BespokeTemplateResponse[],
  id: string
): BespokeTemplateResponse[] {
  return prev.filter(t => t.id !== id);
}

export type { FormSchemaVersionPin } from '../../services/api/types.gen';
export {
  bespokeTemplateMatchesPin,
  enabledTemplatesBySectionForPin,
  filterBespokeTemplatesForPin,
  formSchemaVersionPinKey,
  formSchemaVersionPinKeyFromTemplate,
  liveTemplatesBySectionForPin,
  pinFromFormSchemaVersion,
  pinsFromFormSchemaVersions,
} from './formSchemaVersionPin';

export type SectionValidationStatus = 'pass' | 'fail' | 'missing';

export function sectionValidationStatusFromLayoutState(
  state: SectionLayoutState
): SectionValidationStatus {
  if (state.kind === 'valid') return 'pass';
  if (state.kind === 'invalid') return 'fail';
  return 'missing';
}

/**
 * Validation modal: only live layouts are checked; paused / absent = incomplete.
 */
export function sectionValidationStatusForLiveLayout(
  liveTemplate: BespokeTemplateResponse | undefined,
  questions: FormTreeQuestion[]
): SectionValidationStatus {
  if (!liveTemplate) {
    return 'missing';
  }
  return sectionValidationStatusFromLayoutState(
    getSectionLayoutState(liveTemplate, questions)
  );
}

/**
 * Prefer section_order when present; append any tree keys the FSV omits.
 * Shared by Forms Manager sidebar and Validation Results.
 */
export function orderedFormTreeSectionIds(
  tree: Record<string, unknown>,
  sectionOrder: { section_id: string }[] | null | undefined
): string[] {
  if (sectionOrder && sectionOrder.length > 0) {
    const ordered = sectionOrder
      .map(item => item.section_id)
      .filter(sectionId => sectionId in tree);
    const seen = new Set(ordered);
    for (const key of Object.keys(tree)) {
      if (!seen.has(key)) {
        ordered.push(key);
        seen.add(key);
      }
    }
    if (ordered.length > 0) return ordered;
  }
  return Object.keys(tree);
}
